import { m } from 'framer-motion';
import { useAuth0 } from '@auth0/auth0-react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Toolbar from '@mui/material/Toolbar';
import MenuItem from '@mui/material/MenuItem';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import Logo from 'src/components/logo';
import Iconify from 'src/components/iconify';
import { varHover } from 'src/components/animate';
import CustomPopover, { usePopover } from 'src/components/custom-popover';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

const NAV_ITEMS = [
  { label: 'Agencies', href: paths.admin.agencies, icon: 'solar:buildings-2-bold' },
  { label: 'Billing', href: paths.admin.billing, icon: 'solar:bill-list-bold' },
  { label: 'Audit log', href: paths.admin.audit, icon: 'solar:history-bold' },
  { label: 'Platform admins', href: paths.admin.admins, icon: 'solar:user-id-bold-duotone' },
];

// Minimal, standalone shell for the platform admin portal (epic #45). Deliberately separate from
// the operator DashboardLayout: a platform-admin has no agency, so none of the agency-scoped nav
// applies. Subdomain-ready — it owns its own header and content frame.
export default function AdminLayout({ children }: Props) {
  const { user, logout } = useAuth0();
  const pathname = usePathname();
  const popover = usePopover();

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
    popover.onClose();
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Logo sx={{ mr: 1 }} />

          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Platform Admin
          </Typography>

          <Stack direction="row" spacing={1} sx={{ ml: 2, display: { xs: 'none', md: 'flex' } }}>
            {NAV_ITEMS.map((item) => {
              const active = pathname === item.href || pathname.startsWith(`${item.href}/`);
              return (
                <Button
                  key={item.href}
                  component={RouterLink}
                  href={item.href}
                  size="small"
                  color={active ? 'primary' : 'inherit'}
                  variant={active ? 'soft' : 'text'}
                  startIcon={<Iconify icon={item.icon} />}
                >
                  {item.label}
                </Button>
              );
            })}
          </Stack>

          <Box sx={{ flexGrow: 1 }} />

          <Stack direction="row" spacing={1} alignItems="center">
            <IconButton
              component={m.button}
              whileTap="tap"
              whileHover="hover"
              variants={varHover(1.05)}
              onClick={popover.onOpen}
              sx={{
                width: 40,
                height: 40,
                background: (theme) =>
                  popover.open
                    ? `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`
                    : 'transparent',
              }}
            >
              <Avatar
                src={user?.picture}
                alt={user?.name}
                sx={{
                  width: 36,
                  height: 36,
                  border: (theme) => `solid 2px ${theme.palette.background.default}`,
                }}
              >
                {user?.nickname?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>

            <CustomPopover open={popover.open} onClose={popover.onClose} sx={{ width: 250, p: 0 }}>
              <Box sx={{ p: 2, pb: 1.5 }}>
                <Typography variant="subtitle2" noWrap>
                  {user?.name}
                </Typography>

                <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
                  {user?.email}
                </Typography>
              </Box>

              <Divider sx={{ borderStyle: 'dashed' }} />

              <MenuItem
                onClick={handleLogout}
                sx={{ m: 1, fontWeight: 'fontWeightBold', color: 'error.main' }}
              >
                Logout
              </MenuItem>
            </CustomPopover>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" component="main" sx={{ py: { xs: 3, md: 5 } }}>
        {children}
      </Container>
    </Box>
  );
}
